---
id: rezi-mcp
title: Rezi MCP
owner: tashi
status: Active
lastReviewed: 2026-04-14
visibility: internal
tags:
  - mcp
  - resume
  - oauth
  - jobs
  - microservice
---

## Product Overview

### Who it serves

This service supports signed-in Rezi users who want AI clients such as Claude Code and other streamable HTTP MCP clients to work directly against their Rezi account. A paid subscription is not required.

### Primary workflow

An MCP client connects to the remote `/mcp` endpoint, completes the OAuth 2.1 login flow through the Rezi web app, receives a bearer token, and then uses tools to list resumes, read a resume, create or update a resume, search job listings, and fetch full job details.

### Business rules

Tool usage requires an authenticated Rezi user and is limited to 60 MCP POST requests per account per minute on each service instance. MCP GET and DELETE requests are not included. Resume creation is limited to one create per user per minute to protect against duplicate tool calls. Resume updates are deep-merged over the stored document so partial writes do not erase untouched sections, while new resumes receive the same default structure and customization values the product expects. Job search returns ten listings per page and exposes a separate detail lookup for full descriptions.

### Outcomes

Users can let an AI assistant inspect their existing resumes, tailor them to specific roles, create new resumes that render correctly in Rezi, and pull current job-posting details into the same workflow without leaving the MCP session.

### Out of scope

This service does not render the Rezi UI, manage resume templates in the frontend, or own job-indexing logic. Job search data comes from the search-engine microservice.

## Technical Overview

### Architecture flow

`apps/micro-services/rezi-mcp/cmd/main.go` boots a streamable HTTP MCP server, Firebase Admin SDK clients, persistent OAuth stores, analytics, and the search-engine caller. OAuth discovery and token endpoints are served from the same process, while MCP tool requests flow through bearer-token middleware into tool handlers.

### Entry points

The production binary starts from `apps/micro-services/rezi-mcp/cmd/main.go`. The Docker image is built with `apps/micro-services/rezi-mcp/Dockerfile`, and Cloud Run deployment is driven by `apps/micro-services/rezi-mcp/deploy.sh` plus `.github/workflows/rezi-mcp-push.yml`.

### Key modules

OAuth discovery, authorization, token exchange, and bearer validation live in `apps/micro-services/rezi-mcp/pkg/oauth/handler.go`, with OAuth persistence implemented under `apps/micro-services/rezi-mcp/pkg/firestore`. Resume reads and writes live in `apps/micro-services/rezi-mcp/pkg/firestore/client.go`, including create-time defaults and Lexical HTML field synchronization. MCP tool behavior lives in `apps/micro-services/rezi-mcp/pkg/tools/tools.go`, `apps/micro-services/rezi-mcp/pkg/tools/search_jobs.go`, `apps/micro-services/rezi-mcp/pkg/tools/get_job_details.go`, `apps/micro-services/rezi-mcp/pkg/tools/auto_apply_job.go`, `apps/micro-services/rezi-mcp/pkg/tools/duplicate_resume.go`, `apps/micro-services/rezi-mcp/pkg/tools/cover_letters.go` (cover letter tools; persistence in `apps/micro-services/rezi-mcp/pkg/firestore/cover_letters.go`), and `apps/micro-services/rezi-mcp/pkg/tools/generate_keywords.go` (pro-only, gated in `entitlement.go`). `get_resume_format` and resume-write validation are imported from `apps/micro-services/mcp/pkg/resumes`, which is the canonical Rezi resume content contract. Job-board integration is handled by `apps/micro-services/rezi-mcp/pkg/callers/search_engine.go`, keyword extraction (gateway `/v1/ai/get_ner`) by `apps/micro-services/rezi-mcp/pkg/callers/keyword_targeting.go`, profile picture copies by `apps/micro-services/rezi-mcp/pkg/pictures`, and Amplitude instrumentation lives in `apps/micro-services/rezi-mcp/pkg/analytics/analytics.go`.

### Data sources and sinks

The service reads and writes Rezi resume documents in the Firestore `resumes` collection using the Firebase Admin service account configured through `FIREBASE_CONFIG_{ENV}`. It also calls the search-engine service for job results and ships product analytics events to Amplitude when an API key is configured.

### Integrations and side effects

The OAuth callback verifies Firebase ID tokens and issues short-lived authorization codes. Token exchange creates hashed, revocable MCP credentials with a 30-day expiry through db-api. Resume writes update timestamp fields, apply frontend-compatible defaults on create, and regenerate Lexical HTML mirrors for summary, descriptions, and skills so downstream product rendering stays in sync.

### Constraints and risks

OAuth transaction state and registered clients persist in Firestore, while access credentials persist as hashes through db-api. Because the service uses service-account Firestore access, ownership checks in tool handlers are critical to data isolation. Non-production deployments must set `MCP_PUBLIC_URL` correctly or OAuth metadata will point clients at the wrong base URL.

## Changelog

- 2026-04-14: Added initial internal documentation for the service and documented the current OAuth, subscription, resume, and job-search behavior.
- 2026-07-15: Aligned resume-format guidance and validation with the internal MCP's canonical resume contract.
- 2026-08-31: Made authenticated MCP tool access available without a paid Rezi subscription.
